/**
 * CustomersDashboardPage — Phase 34.10 entry point.
 *
 * Summary tiles + at-risk banner + recent customers feed. Polls every 30 s.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface CustomerSummary {
  id: string;
  email: string;
  tier: string;
  product_name: string;
  health_score: number | null;
  onboarded_at: number | null;
  deal_log_seq: number;
  created_at: number;
}

interface ListCustomersResponse {
  customers: CustomerSummary[];
  total: number;
}

interface ChurnRiskEntry {
  customer_id: string;
  risk_score: number;
  risk_tier: string;
  factors: unknown;
  interventions: unknown;
  escalated: boolean;
  computed_at: number;
}

interface AtRiskResponse {
  customers: ChurnRiskEntry[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchCustomers(): Promise<ListCustomersResponse> {
  return apiFetch<ListCustomersResponse>('/customers');
}

function fetchAtRisk(): Promise<AtRiskResponse> {
  return apiFetch<AtRiskResponse>('/churn/at-risk');
}

function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: fetchCustomers,
    refetchInterval: 30_000,
  });
}

function useAtRisk() {
  return useQuery({
    queryKey: ['churn', 'at-risk'],
    queryFn: fetchAtRisk,
    refetchInterval: 30_000,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export const TIER_COLORS: Record<string, string> = {
  paying:     '#34d399',
  enterprise: '#6ee7b7',
  trial:      '#fbbf24',
  free:       '#94a3b8',
};

export const RISK_COLORS: Record<string, string> = {
  critical: '#f87171',
  high:     '#fb923c',
  medium:   '#fbbf24',
  low:      '#34d399',
};

export function tierColor(t: string): string {
  return TIER_COLORS[t] ?? 'var(--omega-ink-subtle)';
}

// Internal DB value "paying" maps to spec label "Pro". Keep internal value
// stable — renaming requires a DB migration. Display layer translates only.
const TIER_LABELS: Record<string, string> = {
  paying:     'Pro',
  enterprise: 'Enterprise',
  trial:      'Trial',
  free:       'Free',
};
export function displayTier(t: string): string {
  return TIER_LABELS[t] ?? t;
}

export function healthColor(score: number | null): string {
  if (score === null) return 'var(--omega-ink-subtle)';
  if (score >= 70) return '#34d399';
  if (score >= 40) return '#fbbf24';
  return '#f87171';
}

export function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color, highlight }: {
  label: string;
  value: string | number;
  color?: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight
          ? 'rgba(248,113,113,0.08)'
          : 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: highlight
          ? '1px solid rgba(248,113,113,0.4)'
          : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 18px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>
        {value}
      </div>
    </div>
  );
}

function AtRiskBanner({ customers }: { customers: ChurnRiskEntry[] }) {
  if (customers.length === 0) return null;
  return (
    <div
      style={{
        background: 'rgba(248,113,113,0.07)',
        border: '1px solid rgba(248,113,113,0.35)',
        borderRadius: 6,
        padding: '14px 18px',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#f87171',
            boxShadow: '0 0 6px 2px rgba(248,113,113,0.5)',
          }}
        />
        <span style={{ fontSize: '9.5pt', fontWeight: 700, color: '#f87171', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {customers.length} Customer{customers.length !== 1 ? 's' : ''} at High Churn Risk
        </span>
        <Link
          to="/omega/customers/churn"
          style={{ marginLeft: 'auto', fontSize: '8.5pt', color: '#f87171', textDecoration: 'none', fontWeight: 600 }}
        >
          View All →
        </Link>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {customers.slice(0, 3).map(c => (
          <Link
            key={c.customer_id}
            to={`/omega/customers/${c.customer_id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 10px',
              background: 'rgba(248,113,113,0.06)',
              border: '1px solid rgba(248,113,113,0.18)',
              borderRadius: 4,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                fontSize: '7.5pt',
                padding: '1px 6px',
                border: `1px solid ${RISK_COLORS[c.risk_tier] ?? '#f87171'}`,
                color: RISK_COLORS[c.risk_tier] ?? '#f87171',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                fontWeight: 700,
                borderRadius: 3,
              }}
            >
              {c.risk_tier}
            </span>
            <span style={{ flex: 1, fontSize: '9.5pt', color: 'var(--omega-ink)' }}>
              {c.customer_id}
            </span>
            <span style={{ flexShrink: 0, fontSize: '9pt', fontWeight: 700, color: '#f87171' }}>
              {(c.risk_score * 100).toFixed(0)}%
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CustomerRow({ c }: { c: CustomerSummary }) {
  return (
    <Link
      to={`/omega/customers/${c.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: '7.5pt',
          padding: '1px 6px',
          border: `1px solid ${tierColor(c.tier)}`,
          color: tierColor(c.tier),
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 700,
          borderRadius: 3,
        }}
      >
        {displayTier(c.tier)}
      </span>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {c.email}
          {c.product_name ? <span style={{ color: 'var(--omega-ink-subtle)', fontWeight: 400 }}> · {c.product_name}</span> : null}
        </div>
        <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
          {c.onboarded_at ? `Onboarded ${fmtTime(c.onboarded_at)}` : 'Not yet onboarded'}
        </div>
      </div>
      <span style={{ flexShrink: 0, fontSize: '9pt', fontWeight: 700, color: healthColor(c.health_score) }}>
        {c.health_score !== null ? `${c.health_score.toFixed(0)}` : '—'}
      </span>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CustomersDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data: custData, isPending, isError } = useCustomers();
  const { data: atRiskData } = useAtRisk();

  const customers = custData?.customers ?? [];
  const atRisk = atRiskData?.customers ?? [];

  const counts: Record<string, number> = {};
  for (const c of customers) counts[c.tier] = (counts[c.tier] ?? 0) + 1;
  const onboarded = customers.filter(c => c.onboarded_at !== null).length;
  const recent = customers.slice(0, 8);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Customers' }]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Customer Brain
        </h1>
        {atRisk.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: '#f87171',
              color: '#0a0d12',
              fontSize: '8pt',
              fontWeight: 700,
            }}
          >
            {atRisk.length}
          </span>
        )}
        <Link
          to="/omega/customers/list"
          style={{
            marginLeft: 'auto',
            background: 'rgba(52,211,153,0.12)',
            border: '1px solid rgba(52,211,153,0.35)',
            borderRadius: 4,
            color: '#34d399',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          All Customers
        </Link>
      </div>

      {/* At-risk banner */}
      {!isPending && !isError && <AtRiskBanner customers={atRisk} />}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total" value={custData?.total ?? '—'} />
        <StatCard label="Paying" value={counts.paying ?? 0} color="#34d399" />
        <StatCard label="Trial" value={counts.trial ?? 0} color="#fbbf24" />
        <StatCard label="Onboarded" value={onboarded} color="#7dd3fc" />
        <StatCard label="At Risk" value={atRisk.length} color="#f87171" highlight={atRisk.length > 0} />
      </div>

      {/* Nav strip */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { to: '/omega/customers/list',    label: 'Customers' },
          { to: '/omega/customers/support', label: 'Support' },
          { to: '/omega/customers/churn',   label: 'Churn Risk' },
          { to: '/omega/customers/feedback', label: 'Feedback' },
        ].map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt',
              fontWeight: 600,
              padding: '6px 14px',
              textDecoration: 'none',
              letterSpacing: '0.04em',
            }}
          >
            {label}
          </Link>
        ))}
      </div>

      {/* Recent customers */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 4 }}>
          Recent Customers
        </div>
        {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load customers.</div>}
        {!isPending && !isError && recent.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
            No customers yet — will populate when deals close.
          </div>
        )}
        {recent.map(c => <CustomerRow key={c.id} c={c} />)}
      </section>
    </div>
  );
}
